import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateNotification, useUpdateNotification, CreateNotificationData } from "@/hooks/use-system-notifications-admin";
import { toast } from "sonner";

const notificationSchema = z.object({
  title: z.string().min(1, "Título é obrigatório").max(100, "Título deve ter no máximo 100 caracteres"),
  content: z.string().min(1, "Conteúdo é obrigatório").max(500, "Conteúdo deve ter no máximo 500 caracteres"),
  type: z.enum(["info", "warning", "success", "announcement"]),
  target_audience: z.enum(["all", "company_admins", "specific_companies"]),
  expires_at: z.string().optional(),
});

type NotificationFormData = z.infer<typeof notificationSchema>;

interface NotificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notification?: any;
  mode: "create" | "edit";
}

export function NotificationModal({ open, onOpenChange, notification, mode }: NotificationModalProps) {
  const createNotification = useCreateNotification();
  const updateNotification = useUpdateNotification();

  const form = useForm<NotificationFormData>({
    resolver: zodResolver(notificationSchema),
    defaultValues: {
      title: notification?.title || "",
      content: notification?.content || "",
      type: notification?.type || "info",
      target_audience: notification?.target_audience || "all",
      expires_at: notification?.expires_at ? new Date(notification.expires_at).toISOString().split('T')[0] : "",
    },
  });

  const onSubmit = async (data: NotificationFormData) => {
    try {
      const submitData: CreateNotificationData = {
        title: data.title,
        content: data.content,
        type: data.type,
        target_audience: data.target_audience,
        expires_at: data.expires_at ? new Date(data.expires_at + 'T23:59:59').toISOString() : undefined,
      };

      if (mode === "create") {
        await createNotification.mutateAsync(submitData);
        toast.success("Notificação criada com sucesso!");
      } else {
        await updateNotification.mutateAsync({ 
          id: notification.id, 
          data: submitData 
        });
        toast.success("Notificação atualizada com sucesso!");
      }

      onOpenChange(false);
      form.reset();
    } catch (error) {
      toast.error(mode === "create" ? "Erro ao criar notificação" : "Erro ao atualizar notificação");
    }
  };

  const typeOptions = [
    { value: "info", label: "Informação" },
    { value: "warning", label: "Aviso" },
    { value: "success", label: "Sucesso" },
    { value: "announcement", label: "Anúncio" },
  ];

  const audienceOptions = [
    { value: "all", label: "Todos os usuários" },
    { value: "company_admins", label: "Administradores de empresa" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Nova Notificação" : "Editar Notificação"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título</FormLabel>
                  <FormControl>
                    <Input placeholder="Digite o título da notificação..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Conteúdo</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Digite o conteúdo da notificação..."
                      className="min-h-[100px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {typeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="target_audience"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Público-alvo</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o público" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {audienceOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="expires_at"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data de Expiração (opcional)</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit"
                disabled={createNotification.isPending || updateNotification.isPending}
              >
                {mode === "create" ? "Criar" : "Salvar"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}